'use client'

import { useContext, useEffect, useRef } from 'react'
import { AnalyticsContext } from 'providers/AnalyticsProvider'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { recordBannerEhConsent } from 'utils/consent/recordBannerEhConsent'
import { cookieStringHasConsentBanner } from 'constants/consentBannerCookies'

const BANNER_COPY = [
  'We use and allow cookies and similar tools on our websites to enable essential functionalities and to better understand how visitors use our site (including use of session replay technology), as well as for personalization, social media features, and advertising. Read our',
  'privacy policy',
  'to learn more. By closing this box or by your continued use of our sites, you accept such use.',
]

// Click payloads mirror the legacy <TrackingWrapper> output used by the
// original ConsentBanner. TrackingWrapper always injects `experiments: []`
// via getTrackingObject() and lets `event_type` default to `'click'` in
// trackEvent — we replicate that exact shape here so downstream analytics
// receive identical events.
const CLICK_PAYLOADS = {
  close: {
    event_action: 'ui_click',
    nav_link_section: 'TCB banner',
    list_name: 'Close consent click event',
    experiments: [],
  },
  ok: {
    event_action: 'button_click',
    nav_link_section: 'TCB banner',
    click_text: 'Ok',
    list_name: 'Tracking consent click event',
    experiments: [],
  },
  privacy: {
    event_action: 'link_click',
    nav_link_section: 'TCB banner',
    click_text: 'Privacy policy',
    list_name: 'Policies link click event',
    experiments: [],
  },
} as const

/**
 * StaticConsentBannerTracker — client-only tracking companion for StaticConsentBanner.
 *
 * Renders nothing. Fires the same analytics events the legacy ConsentBanner did:
 *   1. `_views/policy` impression when the banner is visible on page load.
 *   2. Click events when the user dismisses the banner (close / Ok / privacy link).
 *
 * Pre-hydration dismissals (handled by the inline script in layout.tsx) are
 * captured via `window.__consentDismissedBeforeHydration` and replayed once
 * analytics is ready, so no events are lost.
 */
export function StaticConsentBannerTracker() {
  const {
    state: { isPageLoaded, analyticsInstance },
  } = useContext(AnalyticsContext)
  const { fireAndForget } = useTriggerEvents()
  const viewFiredRef = useRef(false)
  const preHydrationFiredRef = useRef(false)

  // Fire the impression event once when the page loads and the banner is visible.
  // Also replay any pre-hydration dismiss click that the inline script captured.
  useEffect(() => {
    if (!isPageLoaded) return

    console.log('[ConsentBannerTracker] Page loaded:', { isPageLoaded })

    const isAcknowledged =
      document.documentElement.classList.contains('consent-acknowledged') ||
      cookieStringHasConsentBanner(document.cookie)

    // If the banner was dismissed before hydration, fire the click event retroactively.
    if (!preHydrationFiredRef.current) {
      const win = window as unknown as Record<string, unknown>
      const preHydrationDismiss = win.__consentDismissedBeforeHydration as string | undefined
      if (preHydrationDismiss) {
        delete win.__consentDismissedBeforeHydration
        preHydrationFiredRef.current = true
        const payload = preHydrationDismiss === 'close' ? CLICK_PAYLOADS.close : CLICK_PAYLOADS.ok
        console.log('[ConsentBannerTracker] Pre-hydration dismiss:', payload.event_action)
        recordBannerEhConsent(
          preHydrationDismiss === 'close' ? 'banner_close' : 'banner_ok',
          analyticsInstance
        )
        fireAndForget({ ...payload })
      }
    }

    if (isAcknowledged) return
    if (viewFiredRef.current) return

    viewFiredRef.current = true
    console.log('[ConsentBannerTracker] Impression: _views/policy')
    fireAndForget({
      event_type: 'app_event',
      event_action: 'app_event',
      nav_link_section: 'TCB banner',
      track_event: '_views/policy',
      disclosure_copy: `${BANNER_COPY[0]} ${BANNER_COPY[1]} ${BANNER_COPY[2]}`,
      experiments: [],
    })
  }, [isPageLoaded, fireAndForget, analyticsInstance])

  // Fire click tracking events when dismiss buttons or the privacy link are clicked.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target?.closest) return

      // Dismiss buttons (close X / Ok)
      const dismissBtn = target.closest('[data-consent-dismiss]')
      if (dismissBtn) {
        const isCloseBtn = dismissBtn.getAttribute('aria-label') === 'Close'
        console.log('[ConsentBannerTracker] Click:', isCloseBtn ? 'close' : 'ok')
        recordBannerEhConsent(isCloseBtn ? 'banner_close' : 'banner_ok', analyticsInstance)
        fireAndForget({
          ...(isCloseBtn ? CLICK_PAYLOADS.close : CLICK_PAYLOADS.ok),
        })
        return
      }

      // Privacy policy link inside the consent banner
      const bannerEl = target.closest('.consent-banner')
      if (bannerEl && target.closest('a')) {
        console.log('[ConsentBannerTracker] Click: privacy link')
        fireAndForget({ ...CLICK_PAYLOADS.privacy })
      }
    }

    document.addEventListener('click', handler, true)
    return () => document.removeEventListener('click', handler, true)
  }, [fireAndForget, analyticsInstance])

  return null
}
